import { useEffect, useRef, useState, useCallback } from 'react';
import api from '../../services/api';

// Carrega o script do Player.js do CDN do Bunny.net (uma única vez na sessão)
function loadPlayerJS() {
  return new Promise((resolve, reject) => {
    // Se já foi carregado, resolve imediatamente
    if (window.playerjs) {
      resolve(window.playerjs);
      return;
    }

    // Se o script já está no DOM mas ainda carregando, aguarda
    const existing = document.querySelector('script[src="//cdn.plyr.io/static/demo/bunny-10s.jpg"]');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.playerjs));
      existing.addEventListener('error', reject);
      return;
    }

    // Injeta o script
    const script = document.createElement('script');
    script.src = '//cdn.embed.ly/player-0.1.0.min.js';
    script.async = true;
    script.onload = () => resolve(window.playerjs);
    script.onerror = () => reject(new Error('Falha ao carregar Player.js'));
    document.head.appendChild(script);
  });
}

export default function VideoPlayer({ video, onProgressUpdate, onComplete }) {
  const iframeRef = useRef(null);
  const playerRef = useRef(null);        // instância do playerjs.Player
  const [progress, setProgress] = useState(0);
  const [canComplete, setCanComplete] = useState(false);
  const [isLoadingProgress, setIsLoadingProgress] = useState(true);
  const [duration, setDuration] = useState(0);

  // Refs para valores que precisam ser lidos dentro de callbacks sem re-render
  const maxProgressRef = useRef(0);      // maior % já atingida (não regride com seek)
  const lastSavedTimeRef = useRef(0);    // último watched_seconds enviado ao backend
  const durationRef = useRef(0);         // espelho de duration para usar em callbacks
  const saveIntervalRef = useRef(null);
  const videoRef = useRef(null);         // vídeo atual (para evitar stale closure)

  // Mantém durationRef sincronizado com o state
  useEffect(() => {
    durationRef.current = duration;
  }, [duration]);

  // ─── Salva progresso no backend ─────────────────────────────────────────────
  const saveProgress = useCallback(async (watchedSeconds, durationSeconds) => {
    if (!videoRef.current || durationSeconds === 0) return;

    // Evita salvar se a diferença for menor que 2s
    if (Math.abs(watchedSeconds - lastSavedTimeRef.current) < 2) return;

    try {
      await api.post('/progress', {
        video_id: videoRef.current.id,
        watched_seconds: watchedSeconds,
        duration_seconds: durationSeconds,
      });
      lastSavedTimeRef.current = watchedSeconds;
      console.log(`✅ Progresso salvo: ${watchedSeconds}s / ${durationSeconds}s`);
    } catch (err) {
      console.error('❌ Erro ao salvar progresso:', err);
    }
  }, []);

  // ─── Inicializa o Player.js quando o vídeo (e o iframe) mudam ───────────────
  useEffect(() => {
    if (!video) return;

    console.log('🔄 Inicializando player para vídeo:', video.id);

    // Guarda referência do vídeo atual nas refs
    videoRef.current = video;

    // Reseta estados
    setProgress(0);
    setCanComplete(false);
    setIsLoadingProgress(true);
    setDuration(0);
    maxProgressRef.current = 0;
    lastSavedTimeRef.current = 0;
    durationRef.current = 0;

    // Destrói player anterior
    if (playerRef.current) {
      try { playerRef.current.off('timeupdate'); } catch (_) {}
      try { playerRef.current.off('ended'); } catch (_) {}
      playerRef.current = null;
    }

    // Para o auto-save anterior
    if (saveIntervalRef.current) {
      clearInterval(saveIntervalRef.current);
      saveIntervalRef.current = null;
    }

    // ── Fluxo principal ──────────────────────────────────────────────────────
    let cancelled = false;

    async function init() {
      // 1. Garante que Player.js está carregado
      let playerjs;
      try {
        playerjs = await loadPlayerJS();
      } catch (err) {
        console.error('❌ Player.js não carregou:', err);
        setIsLoadingProgress(false);
        return;
      }

      if (cancelled) return;

      // 2. Carrega progresso salvo no Supabase
      let savedSeconds = 0;
      try {
        const { data } = await api.get(`/progress/video/${video.id}`);
        if (data?.watched_seconds > 0) {
          savedSeconds = data.watched_seconds;
          lastSavedTimeRef.current = savedSeconds;
          console.log(`📥 Progresso salvo encontrado: ${savedSeconds}s`);

          // Calcula progresso inicial se temos a duração no objeto vídeo
          if (video.duration_seconds > 0) {
            const initialPct = (savedSeconds / video.duration_seconds) * 100;
            maxProgressRef.current = initialPct;
            setProgress(initialPct);
            setCanComplete(initialPct >= 90);
            setDuration(video.duration_seconds);
            durationRef.current = video.duration_seconds;
          }
        }
      } catch (err) {
        console.error('❌ Erro ao carregar progresso:', err);
      }

      if (cancelled) return;
      setIsLoadingProgress(false);

      // 3. Aguarda o iframe estar no DOM e inicializa o Player.js
      const iframe = iframeRef.current;
      if (!iframe) return;

      const player = new playerjs.Player(iframe);
      playerRef.current = player;

      player.on('ready', () => {
        console.log('✅ Player.js pronto');

        // Obtém duração real do player
        player.getDuration((dur) => {
          if (cancelled) return;
          console.log(`⏱️ Duração real: ${dur}s`);
          setDuration(dur);
          durationRef.current = dur;

          // Recalcula progresso inicial com duração real
          if (savedSeconds > 0 && dur > 0) {
            const initialPct = (savedSeconds / dur) * 100;
            maxProgressRef.current = initialPct;
            setProgress(initialPct);
            setCanComplete(initialPct >= 90);
            
            // Retomar vídeo de onde parou
            player.setCurrentTime(savedSeconds);
            console.log(`⏩ Retomando vídeo em ${savedSeconds}s`);
          }
        });

        // 4. Tracking de progresso real via timeupdate
        player.on('timeupdate', ({ seconds }) => {
          if (cancelled) return;
          const dur = durationRef.current;
          if (!dur || dur === 0) return;

          const pct = (seconds / dur) * 100;

          // Só avança — nunca regride (seek para trás não diminui progresso)
          if (pct > maxProgressRef.current) {
            maxProgressRef.current = pct;
            setProgress(pct);
            const completed = pct >= 90;
            setCanComplete(completed);

            if (onProgressUpdate) onProgressUpdate(pct);

            console.log(`📊 ${pct.toFixed(1)}% (${Math.floor(seconds)}s / ${dur}s)`);
          }
        });

        // 5. Salva 100% quando o vídeo termina
        player.on('ended', () => {
          if (cancelled) return;
          const dur = durationRef.current;
          maxProgressRef.current = 100;
          setProgress(100);
          setCanComplete(true);

          if (onProgressUpdate) onProgressUpdate(100);
          saveProgress(dur, dur);

          console.log('🏁 Vídeo finalizado — progresso 100% salvo');
        });

        // 6. Auto-save a cada 3 segundos (mais responsivo)
        saveIntervalRef.current = setInterval(() => {
          if (cancelled) return;
          const dur = durationRef.current;
          if (!dur || dur === 0) return;

          const watchedSeconds = Math.floor((maxProgressRef.current / 100) * dur);
          saveProgress(watchedSeconds, dur);
        }, 3_000);

        // 7. Salvar ao sair da página (beforeunload)
        const handleBeforeUnload = () => {
          const dur = durationRef.current;
          if (!dur || dur === 0) return;
          
          const watchedSeconds = Math.floor((maxProgressRef.current / 100) * dur);
          
          // Usar sendBeacon para garantir que envia mesmo ao fechar
          const data = JSON.stringify({
            video_id: videoRef.current.id,
            watched_seconds: watchedSeconds,
            duration_seconds: dur
          });
          
          navigator.sendBeacon(`${api.defaults.baseURL}/progress`, data);
        };
        
        window.addEventListener('beforeunload', handleBeforeUnload);

        // Cleanup do beforeunload
        return () => {
          window.removeEventListener('beforeunload', handleBeforeUnload);
        };
      });
    }

    init();

    // Cleanup quando o vídeo mudar ou componente desmontar
    return () => {
      cancelled = true;
      if (playerRef.current) {
        try { playerRef.current.off('timeupdate'); } catch (_) {}
        try { playerRef.current.off('ended'); } catch (_) {}
        playerRef.current = null;
      }
      if (saveIntervalRef.current) {
        clearInterval(saveIntervalRef.current);
        saveIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video?.id]);

  // ─── Marca vídeo como concluído manualmente ──────────────────────────────────
  const handleComplete = async () => {
    if (!canComplete) return false;

    try {
      await api.post('/progress/complete', { video_id: video.id });
      if (onComplete) onComplete();
      return true;
    } catch (err) {
      console.error('❌ Erro ao marcar como concluído:', err);
      return false;
    }
  };

  // Expõe para o componente pai via ref do iframe (mantém compatibilidade)
  if (video && iframeRef.current) {
    iframeRef.current.canComplete = canComplete;
    iframeRef.current.handleComplete = handleComplete;
  }

  // ─── Render ──────────────────────────────────────────────────────────────────
  if (!video) {
    return (
      <div className="aspect-video bg-background-900 rounded-2xl flex items-center justify-center border border-gray-700">
        <p className="text-text-secondary">Selecione um vídeo para assistir</p>
      </div>
    );
  }

  return (
    <div className="space-y-2 md:space-y-4">
      <div
        className="bg-black rounded-lg md:rounded-2xl overflow-hidden border border-gray-700 relative"
        style={{ paddingBottom: '56.25%', position: 'relative', height: 0 }}
      >
        {isLoadingProgress && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 md:h-12 md:w-12 border-b-2 border-primary-500 mx-auto mb-2" />
              <p className="text-white text-xs md:text-sm">Carregando...</p>
            </div>
          </div>
        )}

        <iframe
          key={video.id}
          ref={iframeRef}
          title="Video Player"
          src={`${video.embedUrl}&_t=${video.id}`}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
        />
      </div>

      {/* Progresso oculto - apenas para tracking interno */}
      <div className="hidden">
        Progresso: {Math.round(progress)}%
      </div>
    </div>
  );
}
